import { v4 as uuid } from 'uuid'
import {
  ensureRuleRowsWithIds,
  resolveFocusRowIndex,
} from './semantic-row-model'

const DEBUG_SEMANTIC_PASTE =
  typeof process !== 'undefined' && process.env.NODE_ENV === 'development'

/** Last focused semantic row — rowId + field only (not content truth). */
let lastRowFocusMeta = null

export function registerRowFocusMeta(meta) {
  if (!meta?.rowId) return
  lastRowFocusMeta = { rowId: meta.rowId, field: meta.field ?? 'text' }
}

export function clearRowFocusMeta() {
  lastRowFocusMeta = null
}

function debugPaste(label, payload) {
  if (!DEBUG_SEMANTIC_PASTE) return
  console.log(`[semantic-paste] ${label}`, payload)
}

function isRuleEmpty(rule) {
  if (!rule || typeof rule !== 'object') return true
  if (rule.mantra != null) return !String(rule.mantra).trim()
  if (rule.bold != null) {
    return !String(rule.bold).trim() && !String(rule.text ?? '').trim()
  }
  return !String(rule.text ?? '').trim()
}

function isStepEmpty(step) {
  if (!step || typeof step !== 'object') return true
  return !String(step.title ?? '').trim() && !String(step.detail ?? '').trim()
}

/**
 * @param {'empty-replace' | 'insert-below' | 'append-end'} mode
 */
export function applyRowListPaste({
  rows,
  focus,
  items,
  patchRowAt,
  createRow,
  isRowEmptyAt,
  debugLabel = 'rows',
}) {
  const beforeRows = [...rows]
  const rowIndex = resolveFocusRowIndex(beforeRows, focus)

  if (items.length === 0) {
    return beforeRows
  }

  const next = [...beforeRows]
  let mode = 'append-end'

  if (rowIndex >= 0) {
    mode = isRowEmptyAt(rowIndex) ? 'empty-replace' : 'insert-below'
  }

  const currentRow = rowIndex >= 0 ? next[rowIndex] : null

  debugPaste(`${debugLabel}:apply`, {
    rowIndex,
    rowId: focus?.rowId ?? currentRow?.id ?? null,
    currentRow,
    parsedItems: items,
    beforeRows,
    rowKeys: beforeRows.map((r) => r?.id),
    mode,
  })

  if (mode === 'empty-replace') {
    const [first, ...remaining] = items
    next[rowIndex] = patchRowAt(next[rowIndex], first)
    if (remaining.length > 0) {
      next.splice(rowIndex + 1, 0, ...remaining.map((item) => createRow(item)))
    }
  } else if (mode === 'insert-below') {
    next.splice(rowIndex + 1, 0, ...items.map((item) => createRow(item)))
  } else {
    next.push(...items.map((item) => createRow(item)))
  }

  debugPaste(`${debugLabel}:result`, {
    afterRows: next,
    rowKeys: next.map((r) => r?.id),
  })

  return next
}

function resolveRulesFocus(focus, rules) {
  const rowIndex = resolveFocusRowIndex(rules, focus)
  if (rowIndex < 0) return null
  return {
    rowIndex,
    rowId: rules[rowIndex]?.id,
    field: focus?.field ?? 'text',
    isEmpty: isRuleEmpty(rules[rowIndex]),
  }
}

function resolveStepsFocus(focus, steps) {
  const rowIndex = resolveFocusRowIndex(steps, focus)
  if (rowIndex < 0) return null
  if (focus?.field && focus.field !== 'title') {
    return { rowIndex, rowId: steps[rowIndex]?.id, field: focus.field, isEmpty: false }
  }
  return {
    rowIndex,
    rowId: steps[rowIndex]?.id,
    field: 'title',
    isEmpty: isStepEmpty(steps[rowIndex]),
  }
}

export function pasteIntoRules(parsed, currentData, focus) {
  const existing = ensureRuleRowsWithIds(
    Array.isArray(currentData.rules) ? currentData.rules : [],
  )
  const items = parsed.items.map((text) => ({ text: String(text).trim() }))
  const resolvedFocus = resolveRulesFocus(focus, existing)

  return applyRowListPaste({
    rows: existing,
    focus: resolvedFocus,
    items,
    isRowEmptyAt: (i) => isRuleEmpty(existing[i]),
    patchRowAt: (row, item) => {
      const text = item?.text ?? String(item ?? '').trim()
      if (row?.mantra != null) return { ...row, mantra: text }
      return { ...row, text }
    },
    createRow: (item) => {
      const text = item?.text ?? String(item ?? '').trim()
      return { id: uuid(), text }
    },
    debugLabel: 'rules',
  })
}

export function pasteIntoStepsTitles(parsed, currentData, focus) {
  const existing = Array.isArray(currentData.steps) ? currentData.steps : []
  const items = parsed.items.map((text) => String(text).trim())
  const resolvedFocus = resolveStepsFocus(focus, existing)

  return applyRowListPaste({
    rows: existing,
    focus: resolvedFocus,
    items,
    isRowEmptyAt: (i) => isStepEmpty(existing[i]),
    patchRowAt: (row, title) => ({
      ...row,
      title: String(title).trim(),
    }),
    createRow: (title) => ({
      id: uuid(),
      title: String(title).trim(),
      detail: '',
      done: false,
    }),
    debugLabel: 'steps',
  })
}

/**
 * Row identity from paste event — index + field only; emptiness from store data.
 * @param {ClipboardEvent} [event]
 */
export function getRowFocusMetaFromDom(event) {
  let row = null

  if (event?.target instanceof Element) {
    row = event.target.closest('[data-nb-semantic-row]')
  }

  if (!row) {
    const active = document.activeElement
    if (active instanceof Element) {
      row = active.closest('[data-nb-semantic-row]')
    }
  }

  if (!row && lastRowFocusMeta) {
    debugPaste('focus', { ...lastRowFocusMeta, source: 'lastRowFocusMeta' })
    return lastRowFocusMeta
  }

  if (!row) {
    debugPaste('focus', { rowId: null, reason: 'no semantic row' })
    return null
  }

  const rowId = row.getAttribute('data-nb-row-id')
  if (!rowId) {
    debugPaste('focus', { rowId: null, reason: 'missing data-nb-row-id' })
    return null
  }

  const field = row.getAttribute('data-nb-row-field') || 'text'
  const rowIndex = Number(row.getAttribute('data-nb-row-index'))
  const meta = {
    rowId,
    field,
    ...(Number.isNaN(rowIndex) ? {} : { rowIndex }),
  }

  debugPaste('focus', meta)
  return meta
}

export { isRuleEmpty, isStepEmpty, debugPaste }
